import React, {useState, useEffect, useContext, useMemo} from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import {compose, bindActionCreators} from 'redux'
import {Button, Grid, Typography, withStyles} from '@material-ui/core'
import {connect} from 'react-redux'
import AddIcon from '@material-ui/icons/Add'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import useModalState from '../../../hooks/useModalState'

import AudienceErrorModal from './AudienceErrorModal'
import AudienceSelectionModal from './AudienceSelectionModal'
import AudienceGroupCard from './AudienceGroupCard'
import ConfirmationModal from '../../common/ConfirmationModal'
import Loading from '../../common/Loading'
import SecondaryButton from '../../common/SecondaryButton'
import InfoTooltip from '../../common/InfoTooltip'

import NewTestContext from '../context/NewTestContext'

import pageUtil from '../../utils/PageUtils'

import {contextFields} from '../../constants/NewTest'

import {buildCopyName, findMatchingGroups, getMatchingGroupsMessage} from './audienceGroupUtils'

import {createError} from '../../common/redux/actions.notifications'

import utils, {findCaseTypeByName} from '../utils'

import styles from '../styles/TestSetup.styles'
import NewTestErrorContext from '../context/NewTestErrorContext'

const AudienceSetup = props => {
  const {
    classes,
    isActive,
    audienceLocales,
    audienceDemographics,
    caseTypes,
    isFetching,
    nextStep,
    configs,
    registeredFrom,
  } = props

  const {
    audienceGroups,
    caseType,
    setStateField,
    hasPassed,
    setHasPassed,
    hasOpenedDefaultTooltip,
    setHasOpenedDefaultTooltip,
    isCheckingAudienceGroups,
  } = useContext(NewTestContext)

  const {audienceGroupErrors, handleAudienceGroupErrors, hasAudienceGroupErrors} = useContext(
    NewTestErrorContext,
  )

  const [currentGroup, setCurrentGroup] = useState(-1)
  const [demographicFields, setDemographicFields] = useState({})
  const [stepInfoTooltipOpen, setStepInfoTooltipOpen] = useState(false)
  const [groupToDelete, setGroupToDelete] = useState(null)
  const [duplicatedGroupId, setDuplicatedGroupId] = useState(null)
  const [matchingGroupsMessage, setMatchingGroupsMessage] = useState(null)

  const isLucidUser = utils.isLucidUser(registeredFrom)

  // Derived, not state-plus-effect: it used to be seeded with the global minimum
  // and corrected one render later, so the first paint after re-entering this step
  // reported 50 panelists regardless of the real groups — which made the objective
  // warning below flash on and off, and lagged a render behind every +/- click.
  const totalPanelists = useMemo(
    () => audienceGroups.reduce((total, group) => total + group.totalCompletes, 0),
    [audienceGroups],
  )

  // Advisory only, derived at render: this must never enter validatePanelists()
  // or handleNextClick (those open the blocking modal for the *global* minimum)
  // nor NewTestErrorContext (which disables Publish). A null minPanelists means
  // "no minimum configured" — no warning at any total.
  const selectedCaseType = findCaseTypeByName(caseTypes, caseType)
  const minPanelists = selectedCaseType ? selectedCaseType.minPanelists : null
  const belowObjectiveMinimum =
    !_.isEmpty(audienceGroups) && minPanelists != null && totalPanelists < minPanelists

  const [
    panelistsErrorModalOpen,
    openPanelistsErrorModal,
    closePanelistsErrorModal,
  ] = useModalState(false)
  const [
    matchingGroupsErrorModalOpen,
    openMatchingGroupsErrorModal,
    closeMatchingGroupsErrorModal,
  ] = useModalState(false)
  const [audienceGroupModal, openAudienceGroupModal, closeAudienceGroupModal] = useModalState(false)
  const [confirmDeleteModal, openConfirmDeleteModal, closeConfirmDeleteModal] = useModalState(false)

  useEffect(() => {
    if (!hasOpenedDefaultTooltip.audienceSetup) {
      setStepInfoTooltipOpen(true)
      setHasOpenedDefaultTooltip({...hasOpenedDefaultTooltip, audienceSetup: true})
    }
  }, [isLucidUser])

  useEffect(() => {
    setDemographicFields(utils.decamelizeAudienceDemographicKeys(audienceDemographics))
  }, [audienceDemographics])

  useEffect(() => {
    if (audienceGroups.length) {
      setStateField(
        contextFields.audienceGroups,
        audienceGroups.map(group => ({
          ...group,
          totalCompletes: configs.panelistsGroupDefaultValue,
        })),
      )
    }
  }, [configs.panelistsGroupDefaultValue]) // CHECK THIS METHOD

  const removeGroup = index => {
    const removedGroup = audienceGroups[index]
    const newGroups = audienceGroups.filter((_, groupIndex) => index !== groupIndex)

    // clear the gone group's feasibility error, or `hasAudienceGroupErrors()`
    // stays truthy with no card left to fix it on — which duplication makes
    // easy to hit, since a copy inherits the original's error
    if (removedGroup) {
      handleAudienceGroupErrors({[removedGroup.id]: false})
    }

    setStateField(contextFields.audienceGroups, newGroups)
  }

  const handleAddGroup = () => {
    setCurrentGroup(audienceGroups.length + 1)
    openAudienceGroupModal()
  }

  const handleDuplicateGroup = index => {
    const original = audienceGroups[index]
    // cloneDeep, not a spread: demographics and each subDemographics array
    // must not be shared, or editing one group's qualifiers would change the
    // other's
    const copy = {
      ..._.cloneDeep(original),
      id: _.uniqueId(),
      name: buildCopyName(
        original.name,
        audienceGroups.map(({name}) => name),
      ),
    }

    const newGroups = [...audienceGroups]
    newGroups.splice(index + 1, 0, copy)

    // identical selections are identically (in)feasible, so the copy shows
    // the same banner instead of silently looking fulfillable
    handleAudienceGroupErrors({[copy.id]: audienceGroupErrors[original.id] || false})
    setStateField(contextFields.audienceGroups, newGroups)
    setDuplicatedGroupId(copy.id)
  }

  const validatePanelists = () => {
    return totalPanelists >= configs.campaignMinimumPanelists
  }

  const handleNextClick = async () => {
    if (!validatePanelists()) {
      openPanelistsErrorModal()
      return
    }

    // the next step is checkout, so two groups asking for the same panelists
    // with the same selections are stopped here
    const matchingGroups = findMatchingGroups(audienceGroups)

    if (matchingGroups) {
      setMatchingGroupsMessage(getMatchingGroupsMessage(matchingGroups))
      openMatchingGroupsErrorModal()
      return
    }

    pageUtil.scrollToTop()
    setHasPassed({...hasPassed, audienceSetup: true})
    nextStep()
  }

  const handleEditGroup = groupIndex => {
    setCurrentGroup(groupIndex)
    openAudienceGroupModal()
  }

  const getTotalPanelistsClasses = () => {
    let totalPanelistsClass = classes.totalPanelists

    if (!validatePanelists()) {
      totalPanelistsClass += ` ${classes.invalidPanelists}`
    }

    return totalPanelistsClass
  }

  const renderNextButton = () => {
    if (isActive && !hasAudienceGroupErrors()) {
      return (
        <Button
          variant="contained"
          color="primary"
          className={classes.nextButton}
          onClick={handleNextClick}
          style={{
            marginTop: '0',
          }}
        >
          Next
          <ChevronRightIcon fontSize="small" style={{marginLeft: 8}} />
        </Button>
      )
    }
    return null
  }

  return (
    <Grid item lg={3} md={6} sm={12} className={classes.newTestSection}>
      {isFetching || isCheckingAudienceGroups ? (
        <Loading />
      ) : (
        <>
          <AudienceErrorModal
            open={panelistsErrorModalOpen}
            onClose={closePanelistsErrorModal}
            text={`In order to run a successful test we require at least ${configs.campaignMinimumPanelists} panelists.`}
          />
          {matchingGroupsMessage && (
            <AudienceErrorModal
              open={matchingGroupsErrorModalOpen}
              onClose={closeMatchingGroupsErrorModal}
              text={matchingGroupsMessage.text}
              secondaryText={matchingGroupsMessage.secondaryText}
            />
          )}
          <ConfirmationModal
            open={confirmDeleteModal}
            text="Are you sure you want to delete?"
            onCancel={() => {
              setGroupToDelete(null)
              closeConfirmDeleteModal()
            }}
            onConfirm={() => {
              removeGroup(groupToDelete)
              closeConfirmDeleteModal()
            }}
          />
          {audienceGroupModal && (
            <AudienceSelectionModal
              isOpen={audienceGroupModal}
              audienceLocales={audienceLocales}
              closeModal={closeAudienceGroupModal}
              demographicFields={demographicFields}
              group={audienceGroups[currentGroup]}
              currentGroup={currentGroup}
              configs={configs}
            />
          )}

          <Grid className={classes.titleSection} style={{marginBottom: 8}}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Select Audience
              <InfoTooltip
                text={
                  <span>
                    {`Your test includes the selection of ${configs.campaignMinimumPanelists} panelists.`}
                    <br />
                    <br />
                    {`You can create one group of ${configs.campaignMinimumPanelists} and the responses will fall as they may
                        based on your demographic selections.`}
                    <br />
                    <br />
                    To compare results by demographics, you can separate your panelists into
                    multiple groups.
                    <br />
                    <br />
                    {`Each group must have a minimum of ${configs.minimumPanelistsPerGroup} panelists.`}
                    <br />
                    <br />
                    {`An additional cost of ${utils.formatValue(
                      configs.pricePerMinute,
                    )} per panelist, per minute of video, is added if
                        selecting more than ${configs.campaignMinimumPanelists} total panelists.`}
                  </span>
                }
                placement="right"
                open={stepInfoTooltipOpen}
                onOpen={() => setStepInfoTooltipOpen(true)}
                onClose={() => setStepInfoTooltipOpen(false)}
              />
            </Typography>
          </Grid>

          {/* group cards */}
          {!_.isEmpty(audienceGroups) ? (
            <Grid className={classes.audienceGroupsContainer}>
              {audienceGroups.map((group, index) => (
                <AudienceGroupCard
                  key={group.id}
                  group={group}
                  showGroupCost={utils.isLucidUser(registeredFrom)}
                  editGroup={() => handleEditGroup(index)}
                  duplicateGroup={() => handleDuplicateGroup(index)}
                  deleteGroup={() => {
                    openConfirmDeleteModal()
                    setGroupToDelete(index)
                  }}
                  shouldScrollIntoView={group.id === duplicatedGroupId}
                  onScrolledIntoView={() => setDuplicatedGroupId(null)}
                />
              ))}
            </Grid>
          ) : (
            <Typography className={classes.emptyGroupsMessage}>
              To test and compare specific demographics create your individual groups.
            </Typography>
          )}

          <SecondaryButton
            onClick={handleAddGroup}
            style={{
              width: 'max-content',
              alignSelf: _.isEmpty(audienceGroups) ? 'flex-start' : 'center',
              marginBottom: isActive ? 16 : 0,
              marginTop: 16,
              backgroundColor: '#017eff14',
            }}
          >
            <AddIcon fontSize="small" style={{marginRight: '8px'}} />
            Add New Group
          </SecondaryButton>

          {belowObjectiveMinimum && (
            <Typography variant="body1" className={classes.objectiveMinimumWarning}>
              {`${caseType} tests expect a minimum of ${minPanelists} total panelists. You can still continue with fewer, but results may be less reliable.`}
            </Typography>
          )}

          <Grid
            container
            className={classes.audienceBottomSection}
            style={isActive ? {} : {justifyContent: 'center'}}
          >
            <p
              className={classes.totalPanelists}
              style={!isActive ? {marginTop: 16} : {marginTop: 0}}
            >
              {`Total Panelists: `}
              <span className={getTotalPanelistsClasses()}>{totalPanelists}</span>
              <InfoTooltip
                text={`In order to create a successful test, you need at least ${configs.campaignMinimumPanelists} total panelists.`}
                placement="top"
              />
            </p>
            {renderNextButton()}
          </Grid>
        </>
      )}
    </Grid>
  )
}

AudienceSetup.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  isActive: PropTypes.bool.isRequired,
  audienceDemographics: PropTypes.objectOf(PropTypes.object),
  audienceLocales: PropTypes.arrayOf(PropTypes.object),
  caseTypes: PropTypes.arrayOf(PropTypes.object),
  isFetching: PropTypes.bool.isRequired,
  nextStep: PropTypes.func.isRequired,
  configs: PropTypes.objectOf(PropTypes.number),
}

AudienceSetup.defaultProps = {
  audienceDemographics: {},
  audienceLocales: [],
  caseTypes: [],
  configs: {
    campaignMinimumPanelists: 50,
    panelistsGroupDefaultValue: 50,
    minimumPanelistsPerGroup: 50,
    groupPanelistsIncrementDecrementOffset: 25,
    pricePerMinute: 6,
  },
}

const mapStateToProps = ({profile, campaign}) => ({
  registeredFrom: profile?.profile?.registeredFrom,
  audienceDemographics: campaign.audienceDemographics.results,
  audienceLocales: campaign.audienceLocales.results,
  caseTypes: campaign.caseTypes.results,
  isFetching: campaign.audienceDemographics.isFetching,
  configs: campaign.configs,
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      createError,
    },
    dispatch,
  )

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStyles(styles),
)(AudienceSetup)
